/**
 * Clicks whatever an Apple Pencil taps, across the whole console.
 *
 * iPad Safari doesn't reliably turn a Pencil tap into a click, so anything that only
 * listens for click (dropdown options, menu items, clickable rows and cards, and
 * buttons inside the photo annotator) can ignore the Pencil. A Pencil tap on anything else is
 * clicked as soon as the Pencil lifts, and the click Safari may send afterwards is
 * dropped so nothing is picked twice and nothing under a closing list is hit. Fingers,
 * mice and keyboards are left alone.
 */

/** How far the Pencil may drift during a tap before it counts as a scroll. */
const PEN_TAP_SLOP_PX = 10;

/** A press held longer than this is a long-press, not a tap. */
const PEN_TAP_MAX_MS = 600;

/** How long after a Pencil tap Safari's own late click is dropped. */
const LATE_CLICK_MS = 600;

/** How far from the tap Safari's late click may land and still be dropped. */
const LATE_CLICK_RADIUS_PX = 24;

/** Safari already clicks these on a Pencil tap. */
const NATIVE_CONTROL =
  "a[href], input, select, textarea, label, summary, [contenteditable]:not([contenteditable='false'])";

function dropLateClick(x: number, y: number) {
  const drop = (event: MouseEvent) => {
    if (!event.isTrusted) return;
    if (
      Math.hypot(event.clientX - x, event.clientY - y) > LATE_CLICK_RADIUS_PX
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    stop();
  };
  const stop = () => {
    document.removeEventListener("click", drop, true);
    window.clearTimeout(timer);
  };
  document.addEventListener("click", drop, true);
  const timer = window.setTimeout(stop, LATE_CLICK_MS);
}

/** Starts clicking Pencil taps; returns a function that stops it. */
export function installPenTapClicks(): () => void {
  let tap: {
    x: number;
    y: number;
    id: number;
    time: number;
    target: Element;
  } | null = null;

  const onPointerDown = (event: PointerEvent) => {
    tap = null;
    if (event.pointerType !== "pen" || event.button !== 0 || !event.isPrimary) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element) || target.closest(NATIVE_CONTROL)) return;
    tap = {
      x: event.clientX,
      y: event.clientY,
      id: event.pointerId,
      time: event.timeStamp,
      target,
    };
  };

  // Listens after the page's own handlers, so a component that handled the lift
  // itself (and prevented its default) isn't clicked a second time.
  const onPointerUp = (event: PointerEvent) => {
    const start = tap;
    tap = null;
    if (!start || start.id !== event.pointerId || event.defaultPrevented)
      return;
    if (event.timeStamp - start.time > PEN_TAP_MAX_MS) return;
    const drift = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (drift > PEN_TAP_SLOP_PX || !start.target.isConnected) return;
    dropLateClick(event.clientX, event.clientY);
    start.target.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        composed: true,
        view: window,
        detail: 1,
        button: 0,
        clientX: event.clientX,
        clientY: event.clientY,
        screenX: event.screenX,
        screenY: event.screenY,
      }),
    );
  };

  const onPointerCancel = () => {
    tap = null;
  };

  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerCancel, true);
  return () => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerCancel, true);
  };
}
